/* eslint-disable prefer-const */
import * as React from 'react';
import { useFormikContext } from 'formik';
import { styled } from '@mui/material/styles';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormGroup,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { Loader, StepperControls, StepWrapper, StyledCardSection } from '@fnol/library/src/components';
import { common, getApplicationId, getIsWebsite } from '@fnol/library/src/lib';
import { useApi } from '@fnol/library/src/hooks';
import { translations } from '../../lib/consts';
import { getClaimConfirmation, getClaimConfirmationAnonymous } from '../../rpc/actions';
import UserNotificationScreen from '../UserNotificationsScreen';
import SuccessScreen from '../SuccessScreen';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
};

let data = new FormData();

const ConfirmationScreen = ({ navigator }: Props) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<any>();

    const hideStepper = () => {
        const allWithClass = Array.from(document.getElementsByClassName('MuiStepper-root'));

        allWithClass.forEach((element: Element) => {
            if (element instanceof HTMLElement) {
                element.style.display = 'none';
            }
        });
    };

    const { response: claimConfirmation, fetchMethod: fetchClaimConfirmation } = useApi(
        () => getClaimConfirmation(data),
        hideStepper,
    );

    const { response: claimConfirmationAnonymous, fetchMethod: fetchClaimConfirmationAnonymous } = useApi(
        () => getClaimConfirmationAnonymous(data),
        hideStepper,
    );

    const handleSubmit = (): void => {
        data = new FormData();
        data.append('stetaTempId', values.tempID);
        data.append('saglasnost', values.contactAgreement);
        if (getIsWebsite(getApplicationId())) {
            setTouched(
                {
                    oneTimePass: true,
                },
                false,
            );
            const existingErrors: GenericObject = {};
            if (values.oneTimePass === '' || values.oneTimePass === null) {
                existingErrors.oneTimePass = translations.validationMessages.REQUIRED_FIELD;
                setErrors(existingErrors);
            } else {
                data.append('kontakt', values.tempPassContact);
                data.append('sifra', values.oneTimePass);
                fetchClaimConfirmationAnonymous();
            }
        } else {
            fetchClaimConfirmation();
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };
    function createData(policy: string, data: string) {
        return { policy, data };
    }

    const onCheckBoxChange = (e: any) => {
        setFieldValue('contactAgreement', e.target.checked);
    };

    const toDate = (date: string) => {
        return new Date(date);
    };

    const updDate = `${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}.${(
        '0' +
        (toDate(values.dateOfOccurance).getMonth() + 1)
    ).slice(-2)}.${toDate(values.dateOfOccurance).getFullYear()}`;
    const updTime = `${values.hourOfOccurance}:${values.minuteOfOccurance}`;
    const updDateTime = updDate + ', ' + updTime;

    const shouldSendLinkLabel =
        values.personalShouldSendLink === 'true' ? `Da, link se šalje na broj ${values.smsContactMobile}` : 'Ne';

    const rows = [
        createData('Marka i tip vozila', `${values.vehicle.vehicleBrand} ${values.vehicle.vehicleType}`),
        createData('Vreme nastanka', updDateTime),
        createData(
            'Mesto nastanka',
            `${values.myCountry}, ${values.cityInSerbia.label !== '' ? values.cityInSerbia.label : values.city}, 
            ${values.street}`,
        ),
        createData('Uzrok štete', `${values.myCause}`),
        createData('Zahtev podnosi', `${values.personalName} ${values.personalLastName}`),
        createData('Kontakt podaci podnosioca', `${values.personalMobile}, ${values.personalEmail}`),
        createData('Samostalno slikanje', shouldSendLinkLabel),
        createData('Vozilo pokretno', `${values.vehicleMovable === 'true' ? 'Da' : 'Ne'}`),
        createData('Plaćanje', `${values.payment === '1' ? 'Servis' : values.payment === '2' ? 'Tekući račun' : ''}`),
    ];
    return (
        <StepWrapper>
            <Loader openLoader={claimConfirmation.isLoading || claimConfirmationAnonymous.isLoading}></Loader>
            <StyledCardSection>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <>
                        <TableContainer component={Paper} className="table">
                            <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>{translations.POLICY_NUMBER}</StyledTableCell>
                                        <StyledTableCell align="left">{values.policyNumber}</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <StyledTableRow key={row.policy}>
                                            <StyledTableCell component="th" scope="row">
                                                {row.policy}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">{row.data}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography style={{ fontSize: '13px' }}>{translations.confirmationScreen.ENSUREE}</Typography>
                        <Typography style={{ fontSize: '13px' }}>
                            {translations.confirmationScreen.PRIVACY}
                            <a
                                href={`${translations.confirmationScreen.PRIVACY_POLICY_URL}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {translations.confirmationScreen.PRIVACY_POLICY_LINK}
                            </a>
                            {translations.FULL_STOP}
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                name="contactAgreement"
                                control={<Checkbox />}
                                onChange={onCheckBoxChange}
                                label={
                                    <Typography style={{ fontSize: '13px' }}>
                                        {translations.confirmationScreen.AGREEMENT}
                                    </Typography>
                                }
                                componentsProps={{ typography: { fontSize: '13px' } }}
                            />
                        </FormGroup>
                        {getIsWebsite(getApplicationId()) && <UserNotificationScreen tempId={values.tempID} />}
                    </>
                ) : (
                    <SuccessScreen
                        claimId={
                            claimConfirmation.data.data
                                ? claimConfirmation.data.data
                                : claimConfirmationAnonymous.data.data
                        }
                    />
                )}
                {claimConfirmationAnonymous.error.message && !claimConfirmationAnonymous.isSuccess && (
                    <Typography className="confirmation-message">
                        {translations.confirmationScreen.WRONG_PASS}
                    </Typography>
                )}
            </StyledCardSection>
            <>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <StepperControls onPrevious={handleGoBack} onNext={handleSubmit} onNextLabel={common.SEND} />
                ) : null}
            </>
        </StepWrapper>
    );
};

export default ConfirmationScreen;
